/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { licenses, getLicenseByAbbreviation } from "@ndla/licenses";
import config from "../config";
import { AcquireLicensePage } from "../constants";
import type {
  GQLStructuredArticleData_CopyrightFragment,
  GQLStructuredArticleDataFragment,
  GQLStructuredArticleData_AudioLicenseFragment,
  GQLStructuredArticleData_PodcastLicenseFragment,
  GQLStructuredArticleData_BrightcoveLicenseFragment,
  GQLStructuredArticleData_ImageLicenseFragment,
} from "../graphqlTypes";
import type { Author, Breadcrumb } from "../interfaces";

type CopyrightHolder = { "@type": string; name?: string };
type Alignment = {
  "@type": string;
  alignmentType: string;
  educationalFramework: string;
  targetDescription?: string;
  targetName?: string;
  targetUrl?: string;
};
interface StructuredData {
  "@type"?: string;
  "@context"?: string;
  "@id"?: string | null;
  abstract?: string | null;
  audience?: {
    "@type": string;
    educationalRole: string[];
  };
  author?: CopyrightHolder[];
  contentUrl?: string | null;
  contributor?: CopyrightHolder[];
  copyrightHolder?: CopyrightHolder[];
  creator?: CopyrightHolder[];
  creditText?: string;
  copyrightNotice?: string;
  dateCreated?: string;
  dateModified?: string;
  datePublished?: string;
  description?: string | null;
  educationalAlignment?: Alignment[];
  educationalRole?: string;
  embedUrl?: string | null;
  headline?: string;
  identifier?: string;
  image?: string;
  inLanguage?: string;
  itemListElement?: {
    "@type": string;
    name?: string;
    item: string;
    position: number;
  }[];
  learningResourceType?: string[];
  license?: string | null;
  name?: string;
  numberOfItems?: number;
  publisher?: {
    "@type": string;
    name: string;
    legalName: string;
    url: string;
    logo: string;
  };
  thumbnailUrl?: string | null;
  uploadDate?: string | null;
}

interface Mediaelements {
  data:
    | GQLStructuredArticleData_ImageLicenseFragment
    | GQLStructuredArticleData_AudioLicenseFragment
    | GQLStructuredArticleData_PodcastLicenseFragment
    | GQLStructuredArticleData_BrightcoveLicenseFragment
    | null;
  type: string;
}

const CREATIVE_WORK_TYPE = "Article";
const BREADCRUMB_TYPE = "BreadcrumbList";
const ITEM_TYPE = "ListItem";

const PERSON_TYPE = "Person";
const ORGANIZATION_TYPE = "Organization";
const IMAGE_TYPE = "ImageObject";
const VIDEO_TYPE = "VideoObject";
const AUDIO_TYPE = "AudioObject";
const PODCAST_TYPE = "PodcastEpisode";
const AUDIENCE_TYPE = "EducationalAudience";

export const NDLA = {
  "@type": ORGANIZATION_TYPE,
  name: "NDLA",
  legalName: "Nasjonal digital læringsarena",
  url: "https://ndla.no",
  logo: "https://ndla.no/static/logo.png",
};

const structuredDataBase = {
  "@context": "https://schema.org",
};

const mapType = (type: typeof PERSON_TYPE | typeof ORGANIZATION_TYPE, arr?: Author[]) =>
  arr?.map((item) => ({
    "@type": type,
    name: item.name,
  }));

const getCopyrightData = (copyright: GQLStructuredArticleData_CopyrightFragment): StructuredData => {
  const { creators, rightsholders, license, processors } = copyright;
  return {
    license: license?.url,
    author: mapType(PERSON_TYPE, creators),
    copyrightHolder: mapType(ORGANIZATION_TYPE, rightsholders),
    contributor: mapType(PERSON_TYPE, processors),
  };
};
const getCopyrightDataImage = (
  copyright: GQLStructuredArticleData_CopyrightFragment,
  language: string,
): StructuredData => {
  const { creators, rightsholders, license, processors } = copyright;

  const isCopyrighted = license.license === licenses.COPYRIGHTED;

  const licenseUrl = isCopyrighted ? getLicenseByAbbreviation(license.license, language).url : license?.url;

  return {
    license: licenseUrl,
    creator: mapType(PERSON_TYPE, creators),
    copyrightHolder: mapType(ORGANIZATION_TYPE, rightsholders),
    contributor: mapType(PERSON_TYPE, processors),
    ...(rightsholders.length ? { creditText: rightsholders.map((r) => r.name).join(", ") } : {}),
    ...(isCopyrighted
      ? {
          copyrightNotice: rightsholders.map((r) => r.name).join(", "),
        }
      : {}),
  };
};

const getBreadcrumbs = (breadcrumbItems?: Breadcrumb[]): StructuredData | undefined => {
  if (!breadcrumbItems) {
    return undefined;
  }
  const items = breadcrumbItems.map((item, index) => ({
    "@type": ITEM_TYPE,
    name: item.name,
    position: index + 1,
    item: `${config.ndlaFrontendDomain}${item.url}`,
  }));

  return {
    ...structuredDataBase,
    "@type": BREADCRUMB_TYPE,
    numberOfItems: breadcrumbItems.length,
    itemListElement: items,
  };
};

const getAllignments = (article: GQLStructuredArticleDataFragment): Alignment[] | undefined => {
  const core = article.coreElements
    ? article.coreElements?.map((ce) => {
        return {
          "@type": "AlignmentObject",
          alignmentType: "assesses",
          educationalFramework: "LK20",
          targetDescription: ce.title,
          targetName: ce.id,
          targetUrl: `http://psi.udir.no/kl06/${ce.id}`,
        };
      })
    : [];
  const goals = article.competenceGoals
    ? article.competenceGoals?.map((kg) => {
        return {
          "@type": "AlignmentObject",
          alignmentType: "teaches",
          educationalFramework: kg.type,
          targetDescription: kg.title,
          targetName: kg.code || kg.id,
          targetUrl: `http://psi.udir.no/kl06/${kg.code || kg.id}`,
        };
      })
    : [];
  return [...core, ...goals];
};

export const structuredArticleCopyrightFragment = gql`
  fragment StructuredArticleData_Copyright on Copyright {
    license {
      url
      license
    }
    creators {
      name
      type
    }
    processors {
      name
      type
    }
    rightsholders {
      name
      type
    }
    processed
  }
`;

const imageLicenseFragment = gql`
  fragment StructuredArticleData_ImageLicense on ImageLicense {
    src
    title
    copyright {
      ...StructuredArticleData_Copyright
    }
  }
`;

const audioLicenseFragment = gql`
  fragment StructuredArticleData_AudioLicense on AudioLicense {
    src
    title
    copyright {
      ...StructuredArticleData_Copyright
    }
  }
`;

const podcastLicenseFragment = gql`
  fragment StructuredArticleData_PodcastLicense on PodcastLicense {
    src
    title
    description
    copyright {
      ...StructuredArticleData_Copyright
    }
  }
`;

const brightcoveLicenseFragment = gql`
  fragment StructuredArticleData_BrightcoveLicense on BrightcoveLicense {
    src
    title
    cover
    description
    download
    uploadDate
    copyright {
      ...StructuredArticleData_Copyright
    }
  }
`;

export const structuredArticleDataFragment = gql`
  fragment StructuredArticleData on Article {
    id
    title
    metaDescription
    published
    updated
    revised
    supportedLanguages
    copyright {
      ...StructuredArticleData_Copyright
    }
    metaImage {
      image {
        imageUrl
      }
      alttext {
        alttext
      }
    }
    competenceGoals {
      id
      code
      title
      type
    }
    coreElements {
      id
      title
    }
    transformedContent(transformArgs: $transformArgs) {
      metaData {
        images {
          ...StructuredArticleData_ImageLicense
        }
        audios {
          ...StructuredArticleData_AudioLicense
        }
        podcasts {
          ...StructuredArticleData_PodcastLicense
        }
        brightcoves {
          ...StructuredArticleData_BrightcoveLicense
        }
      }
    }
  }
  ${structuredArticleCopyrightFragment}
  ${brightcoveLicenseFragment}
  ${audioLicenseFragment}
  ${podcastLicenseFragment}
  ${imageLicenseFragment}
`;

export const getStructuredDataFromArticle = (
  article: GQLStructuredArticleDataFragment,
  language: string,
  breadcrumbItems?: Breadcrumb[],
) => {
  const inLanguage = article.supportedLanguages?.includes(language)
    ? language
    : (article.supportedLanguages?.[0] ?? language);
  const educationalAlignment = getAllignments(article);
  const articleData: StructuredData = {
    ...structuredDataBase,
    "@type": CREATIVE_WORK_TYPE,
    "@id": `${config.ndlaFrontendDomain}/article/${article.id}`,
    identifier: `${config.ndlaFrontendDomain}/article/${article.id}`,
    inLanguage: inLanguage,
    learningResourceType: ["text"],
    name: article.title,
    headline: article.title,
    abstract: article.metaDescription,
    audience: {
      "@type": AUDIENCE_TYPE,
      educationalRole: ["student"],
    },
    description: article.metaDescription,
    dateCreated: article.published,
    datePublished: article.revised,
    dateModified: article.updated,
    educationalAlignment,
    image: article.metaImage?.image.imageUrl,
    thumbnailUrl: article.metaImage?.image.imageUrl,
    publisher: NDLA,
    ...getCopyrightData(article.copyright),
  };

  const crumbs = getBreadcrumbs(breadcrumbItems);
  const structuredData = crumbs ? [articleData, crumbs] : [articleData];

  const metaData = article.transformedContent?.metaData;
  const images = metaData?.images?.map((i) => ({ data: i, type: IMAGE_TYPE }));
  const audios = metaData?.audios?.map((a) => ({ data: a, type: AUDIO_TYPE }));

  const mediaElements: Mediaelements[] = [...(images ?? []), ...(audios ?? [])];
  const podcasts = article.transformedContent?.metaData?.podcasts || [];
  const videos = article.transformedContent?.metaData?.brightcoves || [];

  const mediaData = createMediaData(mediaElements, language);
  const podcastData = createPodcastData(podcasts);
  const videoData = createVideoData(videos);

  return {
    ...structuredDataBase,
    "@graph": [...structuredData, ...mediaData, ...podcastData, ...videoData],
  };
};

const createMediaData = (media: Mediaelements[], language: string): StructuredData[] =>
  media.map((media) => {
    const { data, type } = media;
    const copyrightDataFn =
      type === IMAGE_TYPE
        ? (data: GQLStructuredArticleData_CopyrightFragment) => getCopyrightDataImage(data, language)
        : getCopyrightData;
    return {
      ...structuredDataBase,
      "@type": type,
      "@id": data?.src,
      name: data?.title,
      contentUrl: data?.src,
      acquireLicensePage: AcquireLicensePage,
      ...(data?.copyright ? copyrightDataFn(data?.copyright) : {}),
    };
  });

const createPodcastData = (podcasts: GQLStructuredArticleData_PodcastLicenseFragment[]): StructuredData[] =>
  podcasts.map((podcast) => {
    return {
      ...structuredDataBase,
      "@type": PODCAST_TYPE,
      "@id": podcast?.src,
      name: podcast?.title,
      audio: {
        "@type": AUDIO_TYPE,
        contentUrl: podcast?.src,
      },
      abstract: podcast?.description,
      acquireLicensePage: AcquireLicensePage,
      ...(podcast?.copyright ? getCopyrightData(podcast.copyright) : {}),
    };
  });

const createVideoData = (videos: GQLStructuredArticleData_BrightcoveLicenseFragment[]): StructuredData[] =>
  videos.map((video) => {
    return {
      ...structuredDataBase,
      "@type": VIDEO_TYPE,
      "@id": video?.src,
      name: video?.title,
      embedUrl: video?.src,
      thumbnailUrl: video?.cover,
      description: video?.description,
      acquireLicensePage: AcquireLicensePage,
      uploadDate: video?.uploadDate,
      ...(video?.copyright ? getCopyrightData(video.copyright) : {}),
    };
  });
